import { Injectable, signal, OnDestroy, isDevMode } from '@angular/core';

export type ConnectionStatus = 'online' | 'slow' | 'offline';

@Injectable({
  providedIn: 'root',
})
export class ConnectionStatusService implements OnDestroy {
  private readonly SLOW_THRESHOLD_MS = 5000;
  private readonly PING_INTERVAL_MS = 30000;
  private pingIntervalId: ReturnType<typeof setInterval> | null = null;
  private debugMode = false;
  private checking = false;
  private currentAbortController: AbortController | null = null;

  private readonly onOnline = () => this.handleOnline();
  private readonly onOffline = () => this.handleOffline();
  private readonly onVisibilityChange = () => this.handleVisibilityChange();

  readonly status = signal<ConnectionStatus>('online');
  readonly isOnline = signal(true);
  readonly isSlow = signal(false);

  constructor() {
    this.init();
    this.exposeDebugMethods();
  }

  private init() {
    this.updateOnlineStatus();

    window.addEventListener('online', this.onOnline);
    window.addEventListener('offline', this.onOffline);
    document.addEventListener('visibilitychange', this.onVisibilityChange);

    this.startPingCheck();
  }

  private handleOnline() {
    this.isOnline.set(true);
    void this.checkConnectionSpeed();
  }

  private handleOffline() {
    this.isOnline.set(false);
    this.isSlow.set(false);
    this.status.set('offline');
    this.abortCurrentCheck();
  }

  private handleVisibilityChange() {
    if (document.hidden) {
      this.stopPingCheck();
      this.abortCurrentCheck();
    } else {
      this.startPingCheck();
      if (this.isOnline()) {
        void this.checkConnectionSpeed();
      }
    }
  }

  private updateOnlineStatus() {
    if (navigator.onLine) {
      this.isOnline.set(true);
      void this.checkConnectionSpeed();
    } else {
      this.handleOffline();
    }
  }

  private startPingCheck() {
    this.stopPingCheck();
    this.pingIntervalId = setInterval(() => {
      if (this.isOnline() && !document.hidden) {
        void this.checkConnectionSpeed();
      }
    }, this.PING_INTERVAL_MS);
  }

  private stopPingCheck() {
    if (this.pingIntervalId) {
      clearInterval(this.pingIntervalId);
      this.pingIntervalId = null;
    }
  }

  private abortCurrentCheck() {
    if (this.currentAbortController) {
      this.currentAbortController.abort();
      this.currentAbortController = null;
    }
    this.checking = false;
  }

  private async checkConnectionSpeed(): Promise<void> {
    if (this.debugMode || this.checking) return;

    this.checking = true;
    this.abortCurrentCheck();
    const controller = new AbortController();
    this.currentAbortController = controller;
    const startTime = performance.now();

    try {
      await fetch('/favicon.ico', {
        method: 'HEAD',
        cache: 'no-store',
        signal: controller.signal,
      });

      const duration = performance.now() - startTime;

      if (duration > this.SLOW_THRESHOLD_MS) {
        this.isSlow.set(true);
        this.status.set('slow');
      } else {
        this.isSlow.set(false);
        this.status.set('online');
      }
    } catch {
      if (controller.signal.aborted) return;
      if (!navigator.onLine) {
        this.handleOffline();
      } else {
        this.isSlow.set(true);
        this.status.set('slow');
      }
    } finally {
      if (this.currentAbortController === controller) {
        this.currentAbortController = null;
      }
      this.checking = false;
    }
  }

  ngOnDestroy() {
    this.stopPingCheck();
    this.abortCurrentCheck();
    window.removeEventListener('online', this.onOnline);
    window.removeEventListener('offline', this.onOffline);
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
  }

  /**
   * Expone métodos de debug en window para testing manual.
   * Solo disponible en modo desarrollo.
   * Uso en consola del navegador:
   *   connectionDebug.setSlow()    - Simula conexión lenta
   *   connectionDebug.setOffline() - Simula sin conexión
   *   connectionDebug.setOnline()  - Vuelve a estado normal
   *   connectionDebug.reset()      - Desactiva debug y vuelve a detección real
   */
  private exposeDebugMethods() {
    if (!isDevMode()) return;

    (window as any).connectionDebug = {
      setSlow: () => {
        this.debugMode = true;
        this.abortCurrentCheck();
        this.isOnline.set(true);
        this.isSlow.set(true);
        this.status.set('slow');
      },
      setOffline: () => {
        this.debugMode = true;
        this.abortCurrentCheck();
        this.isOnline.set(false);
        this.isSlow.set(false);
        this.status.set('offline');
      },
      setOnline: () => {
        this.debugMode = true;
        this.abortCurrentCheck();
        this.isOnline.set(true);
        this.isSlow.set(false);
        this.status.set('online');
      },
      reset: () => {
        this.debugMode = false;
        this.updateOnlineStatus();
      },
    };
  }
}
